import React, { useEffect, useRef, useState } from 'react'
import EstradaNeon from '../EstradaNeon'
import SilhuetaBlumenau from '../SilhuetaBlumenau'
import BadgeEdicao from '../BadgeEdicao'
import { comDestaque } from '../../lib/destaque'
import IconeArte from '../../lib/iconeArte'
import { useEntrada, atraso } from '../../lib/entrada'
import { EVENTO, ANCORA_INSCRICAO, TEMAS_HERO } from '../../dados/evento'

/* Cada assunto aponta para um disco do KIT pela chave `icone` que já existia
   no dado — mesmo mecanismo do Palco (ver DISCO_PALCO lá). Chave desconhecida
   cai no pino da rota, que é neutro e nunca fica errado nesta página. */
const DISCO_TEMA = {
  barras: 'ico-barras',
  raio: 'ico-chip',
  megafone: 'ico-foguete',
  chave: 'ico-casa',
  engrenagem: 'ico-engrenagem',
  alvo: 'ico-grupo',
}

function discoTema(nome) {
  return DISCO_TEMA[nome] || 'ico-pino-logo'
}

/**
 * 1ª dobra — o cartaz do evento.
 *
 * REESCRITA EM 11/08 (2ª vez). A versão anterior já tinha a composição certa
 * em duas colunas, mas errava no elemento mais caro da página: o H1 era um
 * LOCKUP DE IMAGEM — a saudação em texto mais o SVG do wordmark. Três
 * consequências que só aparecem quando a peça é vista ao lado do kit impresso:
 *
 *   1. o wordmark é um arquivo de largura fixa, então o título NÃO ESCALA com
 *      a tela. Ele nascia com 440px e ficava com 440px em 1920, o que num
 *      cartaz de evento significa um título tímido perdido num campo enorme;
 *   2. a saudação ("Bem-vindo à") vinha antes e em cima, roubando a primeira
 *      linha de leitura do nome do evento;
 *   3. imagem não quebra em duas linhas. "ROTA DA / EFICIÊNCIA" empilhado é
 *      exatamente o desenho da sacola, da camiseta e do fundo de palco.
 *
 * Agora o título é TEXTO, em Manrope 800 caixa alta, escalando por `clamp` até
 * ~90px. A saudação virou sobrelinha discreta. O wordmark SVG continua no
 * cabeçalho, que é onde uma logo trabalha.
 *
 * A COLUNA DA DIREITA é uma cena montada em camadas, não uma ilustração
 * pronta: céu em degradê, silhueta de Blumenau no horizonte, estrada de neon
 * subindo, pino de destino no alto, estrelas e o Nauta com a bandeira à
 * frente. Montar em camadas é o que permite a cena reagir à largura da tela —
 * uma ilustração achatada num arquivo só teria de ser recortada.
 *
 * PILHA DE 4 ELEMENTOS (12/08): placa, título, apoio, CTA. Chegou a ter oito,
 * e a primeira tela deixava de caber num notebook. Saíram a pílula de tagline
 * (fundida na linha de apoio), a ficha de quatro células (desceu para o
 * contador) e a banda de seis assuntos (redundante com o Palco).
 */
export default function SecaoHero({
  titulo = EVENTO.nome,
  saudacao = EVENTO.heroSaudacao,
  tagline = EVENTO.tagline,
  taglineDestaque = EVENTO.taglineDestaque,
  promessa = EVENTO.heroPromessa,
  edicao = EVENTO.edicao,
  textoCta = 'Garantir minha presença',
  fundo = EVENTO.heroFundo,
  mascote = EVENTO.heroMascote,
  temas = TEMAS_HERO,
}) {
  const [mascoteFalhou, setMascoteFalhou] = useState(false)
  const mascoteRef = useRef(null)
  const ref = useEntrada()

  /* Fecha o hiato de hidratação do onError da arte do mascote — ver o
     comentário completo lá embaixo, junto do elemento. */
  useEffect(() => {
    const img = mascoteRef.current
    if (img && img.complete && img.naturalWidth === 0) setMascoteFalhou(true)
  }, [mascote])

  /* O título quebra em duas linhas por composição, não por acaso da largura
     disponível. Partir na última palavra dá "ROTA DA / EFICIÊNCIA", que é a
     quebra usada em todas as peças físicas. Se alguém trocar o nome por um de
     uma palavra só, `resto` fica vazio e o título sai numa linha, sem quebrar
     nada. */
  const palavras = titulo.trim().split(' ')
  const ultima = palavras.length > 1 ? palavras.pop() : ''
  const resto = palavras.join(' ')

  return (
    <section id="hero" className="hero noite" ref={ref}>
      {/* CÉU — degradê de fundo com o halo azul do lado da cena. Mesmo desenho
          da lâmina de abertura do deck de patrocínio. */}
      <div className="hero-ceu" aria-hidden="true" />
      <div className="hero-grao" aria-hidden="true" />

      <div className="container-site hero-interno">
        <div className="hero-texto">
          {/* <div>, não <p>: BadgeEdicao renderiza um <p> próprio, e <p> dentro
              de <p> é marcação inválida — o navegador FECHA o parágrafo de fora
              ao encontrar o de dentro, e o resultado é a saudação e o carimbo
              em blocos separados, com a margem errada entre eles. */}
          <div className="hero-sobrelinha entra">
            {saudacao && <span className="hero-saudacao">{saudacao}</span>}
            {edicao && <BadgeEdicao cidade={edicao} />}
          </div>

          {/* O nome do evento como objeto gráfico. `display-cartaz` é a escala
              de cartaz definida em global.css (clamp até ~90px). */}
          {/* O nome vai DUAS vezes: uma legível por máquina, numa linha só, e
              outra desenhada em duas linhas com cores diferentes.

              Não é preciosismo. A versão visual quebra com <br>, e `<br>` não
              produz espaço em `textContent` — o título era extraído como "Rota
              daEficiência" (medido). Isso é o que vai para o resumo do
              buscador, para o cartão de compartilhamento e para qualquer leitor
              que colapse a marcação. A cópia oculta carrega o nome correto; a
              visual fica `aria-hidden` para ninguém ouvir duas vezes. */}
          <h1 className="hero-titulo display-cartaz entra" style={atraso(1)}>
            <span className="apenas-leitor">{titulo}</span>
            <span aria-hidden="true">
              {resto}
              {ultima && (
                <>
                  <br />
                  <span className="hero-titulo-eco">{ultima}</span>
                </>
              )}
            </span>
          </h1>

          {/* A tagline vira PLACA: pílula contornada com a seta em disco à
              esquerda. É como ela aparece na arte — uma faixa de sinalização
              logo abaixo do nome do evento, não uma linha de texto solta. */}
          <p className="hero-tagline entra" style={atraso(2)}>
            <span className="hero-tagline-seta" aria-hidden="true">→</span>
            {comDestaque(tagline, taglineDestaque)}
          </p>
          {promessa && <p className="hero-promessa entra" style={atraso(3)}>{promessa}</p>}

          {/* A FICHA DE 4 CÉLULAS SAIU DAQUI em 12/08 e desceu para o contador.
              Dois motivos: o hero acumulava oito elementos empilhados (placa,
              carimbo, título, pílula de tagline, apoio, ficha, CTA e uma banda
              de seis cartões), e "quando · como · quanto · quantas vagas" faz
              par com a contagem regressiva — juntos são a dobra dos fatos do
              evento, separados são duas listas de dados sem dono.

              `FICHA_EVENTO` vive agora em dados/evento.js, consumida por
              SecaoContador. */}
          <div className="hero-acoes entra" style={atraso(4)}>
            <a href={ANCORA_INSCRICAO} className="btn btn-rota hero-cta">
              {textoCta}
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>

        {/* CENA — decorativa inteira, mas EM FLUXO (segunda coluna do grid) e
            não em `position: absolute`. É o que garante que ela nunca cruze
            com o texto em nenhuma largura. */}
        <div className="hero-cena" aria-hidden="true">
          <div className="hero-palco">
            {fundo && (
              <span className="hero-painel-foto" style={{ backgroundImage: `url(${fundo})` }} />
            )}
            <SilhuetaBlumenau className="silhueta hero-silhueta" />
            {/* A estrada JÁ TRAZ os pins de destino nos nós da curva (ver
                EstradaNeon.jsx). Havia um terceiro pino desenhado aqui por
                cima, e o resultado eram três marcadores amontoados no mesmo
                canto — o marcador de destino só significa alguma coisa quando
                é um. */}
            <EstradaNeon className="hero-estrada" />

            {/* O PINO DE DESTINO COM O SÍMBOLO DA APRESENTA.ME (12/08, PDF do
                cliente): "esse nauta com a pista em neon e o ícone de
                localização com o símbolo da apresenta.me".

                O pino que a `EstradaNeon` desenha é uma gota com miolo vazado
                — genérico, e o pedido é justamente que ele seja NOSSO. O disco
                `ico-pino-logo` é o recorte do kit: a mesma gota, com a casa da
                Apresenta.me dentro e o halo já embutido na arte.

                O pino de cima do SVG some por CSS (revisao.css §2) para não
                haver dois marcadores no mesmo ponto; o do meio da curva fica,
                porque é o que dá a ideia de percurso com paradas. */}
            <span className="hero-pino-destino" aria-hidden="true">
              <IconeArte nome="ico-pino-logo" loading="eager" />
            </span>

            <span className="estrela" style={{ top: '8%', left: '16%', '--estrela-tam': '0.9rem' }} />
            <span className="estrela" style={{ top: '20%', left: '84%', '--estrela-tam': '0.6rem', '--estrela-alfa': 0.5 }} />
            <span className="estrela" style={{ top: '42%', left: '7%', '--estrela-tam': '0.5rem', '--estrela-alfa': 0.45 }} />
            <span className="estrela" style={{ top: '61%', left: '93%', '--estrela-tam': '0.55rem', '--estrela-alfa': 0.4 }} />

            {/* O mascote é opcional: sem o arquivo em disco a cena continua
                completa (cidade + estrada + pino + estrelas).

                `onError` porque `alt=""` NÃO colapsa imagem quebrada no
                navegador do cliente (visto em produção) e a caixa tem altura
                cravada — como a URL é editável no Builder, um 404 desenharia a
                moldura de imagem quebrada na 1ª dobra.

                O `useEffect` lá em cima cobre o hiato de hidratação: esta arte
                existe desde o HTML do SSR, então uma falha rápida dispara o
                evento `error` nativo ANTES de o React prender o listener, e o
                `onError` nunca seria chamado. */}
            {mascote && !mascoteFalhou && (
              <img
                ref={mascoteRef}
                className="hero-mascote"
                src={mascote}
                alt=""
                width="420"
                height="631"
                loading="eager"
                fetchpriority="high"
                onError={() => setMascoteFalhou(true)}
              />
            )}
          </div>

        </div>
      </div>

      {/* O CASARIO ENXAIMEL, DE VOLTA E DA LARGURA DA DOBRA (12/08, PDF do
          cliente): "o fundo com transparência ser estas casas neste modelo
          como é tradicional em blumenau, assim como a igreja".

          A silhueta tinha sido escondida no hero em 12/08 porque a foto real
          de Blumenau virou o chão da dobra e as duas cidades se sobrepunham.
          O pedido agora é explícito e o conflito se resolve por POSIÇÃO, não
          por escolha: a foto fica no campo alto (céu e morro), o casario
          desenhado assenta na base, onde o véu do `.hero::before` já leva a
          foto quase ao preto. Uma cidade acaba onde a outra começa.

          `preserveAspectRatio="none"` (ver SilhuetaBlumenau) faz o casario
          esticar na largura toda — é textura de horizonte, e as torres de
          igreja continuam legíveis porque a deformação é só horizontal. */}
      <SilhuetaBlumenau className="silhueta hero-casario" />

      {/* A BANDA DE SEIS ASSUNTOS — PEDIDO EXPLÍCITO DO CLIENTE, 12/08.

          ⚠️ NÃO REMOVER SEM PEDIDO NOVO DO CLIENTE. Este bloco foi retirado
          duas vezes hoje pelo argumento de que repete, em palavra curta, os
          seis temas que o Palco lista por extenso. O argumento é correto e
          mesmo assim não vale, porque o cliente pediu a banda nominalmente,
          com a lâmina do hero na mão:

            "o fundo dos quadros dos elementos como mercado, marketing,
             locação, processos, e gestão com um fundo branco para ter um
             pouquinho mais de destaque"

          Os nomes citados — Mercado, Marketing, Locação, Processos, Gestão —
          são os de `TEMAS_HERO`, não os do Palco ("Panorama do Mercado",
          "Marketing Imobiliário"...). O pedido é sobre ESTA banda, e o que ele
          pede é justamente destaque: daí o fundo branco.

          A repetição com o Palco continua existindo e está resolvida por
          contraste, não por remoção — aqui, seis cartões BRANCOS iguais; lá,
          bento assimétrico sobre navy. Em sequência, um lê como índice do dia
          e o outro como o conteúdo dele.

          As tintas e o miolo navy dos discos estão em neon.css §7. */}
      <div className="container-site hero-temas-faixa">
        <ul className="hero-temas lista-limpa">
          {temas.map(({ rotulo, icone, texto }, i) => (
            <li key={rotulo} className="entra" style={atraso(i, 45)}>
              <span className="hero-tema-icone" aria-hidden="true">
                <IconeArte nome={discoTema(icone)} loading="eager" />
              </span>
              <strong>{rotulo}</strong>
              {texto && <span className="hero-tema-texto">{texto}</span>}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
